/**
 * Sheets 11 and 51, a new run (FR-006 to FR-009, FR-024, FR-027, C-004; U.1,
 * U.3, U.4, GEN.3, GEN.6, B.3, X.10; research §1.13, §1.15, §1.16).
 *
 * The two path cards, then the selected path's body: Path A declares the layout
 * version (v1 only, a declaration, never a detection) and shows the dropzone or
 * the file row, the per-run parameters and the static header card; Path B
 * shows the dropzone or the file row alone, and never mounts the header-params
 * query. The estimate panel follows a validated upload. The limits come from
 * the public layout document through `layoutQuery('v1')`, never a literal
 * (C-004).
 *
 * `?path=` and `?layout=` preselect the path and the layout version once, on
 * mount, and only with a value of their union (sheet 12's "Fix and upload
 * again" links here). Changing the path or the layout version resets the
 * upload: an upload is granted for one path.
 *
 * Mission 9 WP14 T089 (51/9; FR-015; B.1; U11): while `useBillingGate` refuses, the dropzone refuses
 * drops and states the reason; Start is refused in the estimate panel.
 */
import { useId, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { idSegment } from '@/lib/api'
import { useBillingGate } from '@/lib/billing-gate'
import type { LayoutVersion } from '@/lib/api/layout'
import { RUN_PATHS, type RunPath } from '@/lib/api/runs'
import { layoutQuery } from '@/lib/queries'

import { Dropzone, UploadStatus, pathBByteLimit, type DropzoneState } from './new/Dropzone'
import { EstimatePanel } from './new/EstimatePanel'
import { PathCards } from './new/PathCards'
import { EMPTY_PER_RUN, PerRunParameters, filledPerRun, type PerRunErrors, type PerRunValue } from './new/PerRunParameters'
import { StaticHeaderCard } from './new/StaticHeaderCard'
import { useUpload } from './new/useUpload'

const LAYOUT_VERSIONS: readonly LayoutVersion[] = ['v1']

/** `?path=` must be one of RunPath's values, else Path A. */
function initialPath(value: string | null): RunPath {
  return RUN_PATHS.find((p) => p === value) ?? 'generate'
}

/** `?layout=` must be a declared version, else v1. */
function initialLayout(value: string | null): LayoutVersion {
  return LAYOUT_VERSIONS.find((v) => v === value) ?? 'v1'
}

export default function NewRun() {
  const headingId = useId()
  const layoutSelectId = useId()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const [path, setPath] = useState<RunPath>(() => initialPath(params.get('path')))
  // v1 is the only declared version (U.1), so the select cannot change it and no reset is needed yet;
  // a second version adds a setter that resets the upload, as choosePath does.
  const [layoutVersion] = useState<LayoutVersion>(() => initialLayout(params.get('layout')))
  const [perRun, setPerRun] = useState<PerRunValue>(EMPTY_PER_RUN)
  const [perRunErrors, setPerRunErrors] = useState<PerRunErrors>({})
  const [badRunId, setBadRunId] = useState(false)

  const layout = useQuery(layoutQuery(layoutVersion))
  const limits = layout.data?.limits
  const dropzoneState: DropzoneState = layout.isPending ? 'loading' : layout.isError ? 'unavailable' : 'ready'

  const gate = useBillingGate()
  const upload = useUpload(path)
  const state = upload.state

  function reset() {
    upload.remove()
    setBadRunId(false)
  }

  function choosePath(next: RunPath) {
    if (next === path) return
    reset()
    setPath(next)
  }

  async function onFile(file: File, contentType: string) {
    setBadRunId(false)
    const outcome = await upload.start(file, contentType)
    if (outcome.kind !== 'rejected') return
    // A rejected completion lands on sheet 12 (U.2); a malformed run id is refused (S6).
    let to: string | null = null
    try {
      to = `/app/runs/${idSegment(outcome.runId, 'run id')}`
    } catch {
      // InvalidIdError: rendered as the generic failure below.
    }
    if (to === null) setBadRunId(true)
    else navigate(to)
  }

  const showsDropzone = state.kind === 'idle' || state.kind === 'expired' || state.kind === 'failed'
  const uploadArea = (
    <div className="flex flex-col gap-2">
      {badRunId ? (
        <UploadStatus state={{ kind: 'failed', failure: { reason: 'generic' } }} onRetry={reset} onRemove={reset} />
      ) : (
        <UploadStatus state={state} path={path} onRetry={() => void upload.retry()} onRemove={reset} />
      )}
      {showsDropzone ? (
        <Dropzone
          path={path}
          limits={limits}
          state={dropzoneState}
          onRetry={() => void layout.refetch()}
          onFile={(file, contentType) => void onFile(file, contentType)}
          refused={state.kind === 'idle' && state.refused === true}
          fieldError={state.kind === 'idle' ? state.fieldError : undefined}
          billingRefusal={gate.reason}
        />
      ) : null}
    </div>
  )

  return (
    <div className="flex flex-col gap-6">
      <h1 id={headingId} className="text-h1 text-navy-950">
        New run
      </h1>
      <PathCards
        value={path}
        onChange={choosePath}
        labelledBy={headingId}
        admissionCap={limits?.admission_cap}
        pathBBytes={limits === undefined ? undefined : pathBByteLimit(limits.admission_cap)}
        unavailable={dropzoneState === 'unavailable'}
      />
      {path === 'generate' ? (
        <>
          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={layoutSelectId}>Layout version</Label>
              <Select value={layoutVersion}>
                <SelectTrigger id={layoutSelectId}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LAYOUT_VERSIONS.map((v) => (
                    <SelectItem key={v} value={v}>
                      {v}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {uploadArea}
          <PerRunParameters value={perRun} onChange={setPerRun} errors={perRunErrors} />
          <StaticHeaderCard />
        </>
      ) : (
        uploadArea
      )}
      {state.kind === 'validated' && !badRunId ? (
        <EstimatePanel
          uploadId={state.upload.upload_id}
          path={path}
          perRun={path === 'generate' ? filledPerRun(perRun) : {}}
          admissionCap={limits?.admission_cap}
          onFieldErrors={setPerRunErrors}
        />
      ) : null}
    </div>
  )
}
